import React, { useMemo, useState, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import styles from './Stripe.module.scss';
import { BRAND_COLORS } from '../../config';

type Props = {
  handleStripePayment: (cardDetails: any, billing_details: any, stripe: any, elements: any, company_name: any) => Promise<void>;
};

const EMAIL_REGEX = /^[a-zA-Z0-9](?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
const CONSECUTIVE_DOTS_REGEX = /\.{2,}/;

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value) && !CONSECUTIVE_DOTS_REGEX.test(value);
}

export function Stripe(props: Props) {
  const { handleStripePayment } = props;
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [btnDisable, setBtnDisable] = useState(true);

  // Use refs to track current validity without stale closure issues
  const isEmailValidRef = useRef(false);
  const isNameValidRef = useRef(false);
  const isCardCompleteRef = useRef(false);

  const stripe = useStripe();
  const elements = useElements();

  const useOptions = () => {
    const options = useMemo(
      () => ({
        hidePostalCode: true,
        style: {
          base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: '#aab7c4',
            },
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
          },
        },
      }),
      []
    );

    return options;
  };

  const options = useOptions();

  const updateButtonState = () => {
    const allValid = isEmailValidRef.current && isNameValidRef.current && isCardCompleteRef.current;
    setBtnDisable(!allValid);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (isProcessing) return;

    setIsProcessing(true);

    const form = event.currentTarget;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const name = (form.elements.namedItem('name') as HTMLInputElement).value;
    const company_name = (form.elements.namedItem('company_name') as HTMLInputElement).value;

    if (!email || !isValidEmail(email) || !name || !isCardCompleteRef.current) {
      if (!email) {
        setEmailError('Email is required.');
      } else if (!isValidEmail(email)) {
        setEmailError('Email is invalid.');
      }
      setNameError(name ? '' : 'Name is required.');
      setCardNumberError(isCardCompleteRef.current ? '' : 'Card details are required.');
      setIsProcessing(false);
      return;
    }

    const cardElement = elements?.getElement(CardElement);
    if (!stripe || !elements || !cardElement) {
      console.error("Stripe.js hasn't loaded yet");
      setIsProcessing(false);
      return;
    }

    try {
      await handleStripePayment(
        cardElement,
        { name, email, address: { country: 'AU' } },
        stripe,
        elements,
        company_name
      );
    } catch (error) {
      console.error("Payment error:", error);
      setCardNumberError('An error occurred while processing your payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const validate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'email') {
      if (!value) {
        setEmailError('Email is required.');
        isEmailValidRef.current = false;
      } else if (!isValidEmail(value)) {
        setEmailError('Email is invalid.');
        isEmailValidRef.current = false;
      } else {
        setEmailError('');
        isEmailValidRef.current = true;
      }
    } else if (name === 'name') {
      if (!value) {
        setNameError('Name is required.');
        isNameValidRef.current = false;
      } else {
        setNameError('');
        isNameValidRef.current = true;
      }
    }

    updateButtonState();
  };

  const handleCardChange = (event: any) => {
    if (event.complete) {
      isCardCompleteRef.current = true;
      setCardNumberError('');
    } else {
      isCardCompleteRef.current = false;
      setCardNumberError('Card details are required.');
    }

    updateButtonState();
  };

  return (
    <div className={styles.stripeWrapper}>
      <div className={styles.parentDiv}>
        <form onSubmit={handleSubmit}>
          <div className={styles.srComboInputs}>
            <div className={styles.srComboInputsRow}>
              <input 
                type="email" 
                name="email" 
                placeholder="Email" 
                autoComplete="off" 
                className={styles.srInput} 
                onChange={validate}
              />
            </div>
            {emailError && <span className={styles.stripeFormError}>{emailError}</span>}
          </div>

          <div className={styles.srComboInputs}>
            <div className={styles.srComboInputsRow}>
              <CardElement
                className={styles.stripeCardElement}
                options={options}
                onChange={handleCardChange}
              />
            </div>
            {cardNumberError && <span className={styles.stripeFormError}>{cardNumberError}</span>}
          </div>

          <div className={styles.srComboInputs}>
            <div className={styles.srComboInputsRow}>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Name on Card"
                autoComplete="off"
                className={styles.srInput}
                onChange={validate}
              />
            </div>
            {nameError && <span className={styles.stripeFormError}>{nameError}</span>}
          </div>

          <div className={styles.srComboInputs}>
            <div className={styles.srComboInputsRow}>
              <input
                type="text"
                id="company_name"
                name="company_name"
                placeholder="Company Name (Optional)"
                autoComplete="off"
                className={styles.srInput}
              />
            </div>
          </div>

          <button
            type="submit"
            className={styles.payButton}
            disabled={!stripe || isProcessing || btnDisable}
          >
            {isProcessing ? 'Processing...' : 'Pay'}
          </button>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: "13px", color: "grey", paddingTop: "15px", paddingLeft: "10px" }}>
              By making this purchase, I consent to join Fast Courier Autopay. Fast Courier can auto-charge payments to my card, including adjustments, subject to <a style={{ color: BRAND_COLORS.primary }} href="https://fastcourier.com.au/terms-conditions-2" target="_blank" rel="noreferrer">Fast Courier's Standard Operating Procedure.</a>
            </label>
          </div>
        </form>
      </div>
    </div>
  );
}
